import { useState } from "react";
import { Lock, RefreshCw, Trash2 } from "lucide-react";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOIN_LABELS = {
  "trek-sahara": "Trek Sahara",
  "sejour-initiatique": "Séjour initiatique",
  constellations: "Constellation",
  ateliers: "Atelier",
  formation: "Formation",
  autre: "Autre demande",
};

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("jz_admin_key") || "");
  const [messages, setMessages] = useState(null);
  const [subscribers, setSubscribers] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const remove = async (id) => {
    setDeleting(id);
    try {
      const res = await fetch(`${API}/messages/${id}?key=${encodeURIComponent(key)}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch {
      setError(true);
    } finally {
      setDeleting(null);
    }
  };

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${API}/messages?key=${encodeURIComponent(key)}`);
      if (!res.ok) throw new Error();
      setMessages(await res.json());
      try {
        const subRes = await fetch(`${API}/subscribers?key=${encodeURIComponent(key)}`);
        if (subRes.ok) setSubscribers(await subRes.json());
      } catch { /* liste optionnelle */ }
      localStorage.setItem("jz_admin_key", key);
    } catch {
      setError(true);
      setMessages(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-page" className="pt-32 pb-24 px-6 lg:px-12 max-w-5xl mx-auto min-h-screen">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-olive mb-6">Espace Julie</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-ink">Les demandes reçues</h1>
      </Reveal>

      <form onSubmit={load} className="mt-10 flex flex-wrap items-center gap-3" data-testid="admin-form">
        <div className="relative">
          <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-stone2" strokeWidth={1.5} />
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Ta clé d'accès"
            data-testid="admin-key-input"
            className="rounded-full border border-olive-border bg-beige-50 pl-11 pr-5 py-3.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-olive/40"
          />
        </div>
        <button type="submit" disabled={loading} data-testid="admin-load-button" className="inline-flex items-center gap-2 rounded-full bg-olive px-6 py-3.5 text-sm font-medium text-beige-100 transition-all duration-300 hover:bg-olive-dark disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> Voir les demandes
        </button>
      </form>

      {error && <p className="mt-6 text-sm text-terracotta" data-testid="admin-error">Clé incorrecte — réessaie.</p>}

      {messages && (
        <div className="mt-10" data-testid="admin-messages">
          <p className="text-sm text-stone2 mb-6">{messages.length} demande{messages.length > 1 ? "s" : ""} reçue{messages.length > 1 ? "s" : ""}</p>
          {messages.length === 0 && <p className="text-sage">Aucune demande pour le moment.</p>}
          <div className="space-y-4">
            {messages.map((m) => (
              <article key={m.id} className="bg-beige-50 border border-olive-border rounded-2xl p-6" data-testid={`admin-message-${m.id.slice(0, 8)}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-medium text-ink">{m.name}</span>
                    <span className="rounded-full bg-olive-soft border border-olive-border px-3 py-1 text-xs text-olive-dark">
                      {SOIN_LABELS[m.soin] || m.soin || "Autre demande"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-stone2">{new Date(m.created_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</span>
                    <button
                      type="button"
                      onClick={() => remove(m.id)}
                      disabled={deleting === m.id}
                      data-testid={`admin-delete-${m.id.slice(0, 8)}`}
                      aria-label={`Supprimer le message de ${m.name}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-terracotta/40 px-3 py-1.5 text-xs text-terracotta transition-all duration-300 hover:bg-terracotta hover:text-beige-50 disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Supprimer
                    </button>
                  </div>
                </div>
                <p className="text-sm text-sage leading-relaxed">{m.message}</p>
                <div className="mt-4 pt-4 border-t border-olive-border/60 flex flex-wrap gap-x-8 gap-y-1 text-sm">
                  <a href={`mailto:${m.email}`} className="text-olive hover:text-olive-dark">{m.email}</a>
                  {m.phone && <a href={`tel:${m.phone.replace(/\s/g, "")}`} className="text-olive hover:text-olive-dark">{m.phone}</a>}
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {subscribers && (
        <div className="mt-16" data-testid="admin-subscribers">
          <h2 className="font-serif text-3xl font-light text-ink">Liste d'emails (guide)</h2>
          <p className="text-sm text-stone2 mt-2 mb-6">{subscribers.length} inscrit{subscribers.length > 1 ? "s" : ""}</p>
          {subscribers.length === 0 && <p className="text-sage">Aucun inscrit pour le moment.</p>}
          {subscribers.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-olive-border">
              <table className="w-full text-sm" data-testid="admin-subscribers-table">
                <thead className="bg-olive-soft/60 text-olive-dark">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium">E-mail</th>
                    <th className="text-left px-5 py-3 font-medium">Prénom</th>
                    <th className="text-left px-5 py-3 font-medium">Source</th>
                    <th className="text-left px-5 py-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((s) => (
                    <tr key={s.id} className="border-t border-olive-border/60 bg-beige-50">
                      <td className="px-5 py-3"><a href={`mailto:${s.email}`} className="text-olive hover:text-olive-dark">{s.email}</a></td>
                      <td className="px-5 py-3 text-sage">{s.name || "—"}</td>
                      <td className="px-5 py-3 text-sage">{s.source || "site"}</td>
                      <td className="px-5 py-3 text-stone2">{new Date(s.created_at).toLocaleDateString("fr-FR", { dateStyle: "medium" })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
